import { useState, useEffect, useRef } from 'react';
import { useAuth, getCurrentPosition } from 'src/utils';
import { useIsMobile, useMapFilters } from 'src/hooks';
import { UserMarkersLayer } from './layers/user-markers';
import { PoiMarkersLayer } from './layers/poi-markers';
import { RouteLayer } from './layers/route-layer';
import mapboxgl from 'mapbox-gl';
import { useMapStore, useFeatureTravellerStore, useMapFiltersStore } from 'src/stores';
import { MapBackdrop } from 'src/components';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

export default function Map({
    onMarkerClick,
    handleUserClick,
    isDesktopViewExpanded,
    mobileDrawerState
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [loadingFeatures, setLoadingFeatures] = useState(true);

  const { user } = useAuth();
  const isMobile = useIsMobile();

  const { mapInstance, setMapInstance, center, zoom } = useMapStore();

  const favouriteIds = useFeatureTravellerStore((state) => state.favouriteIds);
  const travellers = useFeatureTravellerStore((state) => state.travellers);
  const selectedTraveller = useFeatureTravellerStore((state) => state.selectedTraveller);
  const selectedFeature = useFeatureTravellerStore((state) => state.selectedFeature);

  const selectedTypes = useMapFiltersStore((state) => state.selectedTypes);
  const selectedCountries = useMapFiltersStore((state) => state.selectedCountries);
  const showTravellers = useMapFiltersStore((state) => state.showTravellers);
  const showFriendsOnly = useMapFiltersStore((state) => state.showFriendsOnly);
  const showFavouritesOnly = useMapFiltersStore((state) => state.showFavouritesOnly);
  const showRoutes = useMapFiltersStore((state) => state.showRoutes);

  useEffect(() => {
    if (!mapRef.current || selectedTraveller !== null || selectedFeature !== null) return;

    let leftPadding = isDesktopViewExpanded ? 400 : 0;

    mapRef.current.easeTo({
      padding: {
        top: 0,
        bottom: 0,
        left: leftPadding,
        right: 0,
      },
      duration: 400, 
      easing: t => t 
    });
  }, [isDesktopViewExpanded]);

  useEffect(() => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: 'mapbox://styles/mapbox/streets-v12',
      center,
      zoom,
      minZoom: 2,
      maxZoom: 12,
      attributionControl: false,
      dragRotate: false,
      touchPitch: false
    });

    map.on('load', () => {
      setMapLoaded(true);
    });

    setMapInstance(map);
    mapRef.current = map;

    if (!isMobile) {
      map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    }

    const geolocateControl = new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: false,
        showUserHeading: true,
      });
    map.addControl(geolocateControl, isMobile ? 'bottom-right' : 'top-right');

    map.on('moveend', () => {
      const newCenter = map.getCenter().toArray();
      const newZoom = map.getZoom();
      useMapStore.getState().setCenter(newCenter);
      useMapStore.getState().setZoom(newZoom);
    });

    geolocateControl.on('geolocate', async () => {
      if (!user?.privacy || user.privacy === 'Hidden') return;

      let coords;
      if (user.privacy === 'Pinned') {
        coords = user.pinnedLocation?.coordinates;
      } else if (user.privacy === 'Live') {
        try {
          const { latitude, longitude } = await getCurrentPosition();
          coords = [longitude, latitude];
        } catch (err) {
          console.error('Failed to fetch live coordinates:', err);
          return;
        }
      }

      if (coords) {
        map.flyTo({ center: coords, zoom: 6, speed: 1.2, curve: 1.42 });
      }
    });

    return () => {
      if (map && map.loaded()) {
        //map.remove();
      }
    }
  }, []);

  useMapFilters({
    selectedTypes: selectedTypes,
    selectedCountries: selectedCountries,
    favouriteIds: favouriteIds,
    showFavouritesOnly: showFavouritesOnly
  });

  return (
    <>
      <div ref={mapContainerRef} style={{ height: '100%', width: '100%' }} />

      {showRoutes && (
        <RouteLayer 
          onMarkerClick={onMarkerClick}
          showRoutes={showRoutes}
        />
      )}

      <PoiMarkersLayer 
        setLoadingFeatures={setLoadingFeatures} 
        onMarkerClick={onMarkerClick} />

      <UserMarkersLayer
        travellers={travellers}
        showTravellers={showTravellers}
        showFriendsOnly={showFriendsOnly}
        user={user}
        handleUserClick={handleUserClick}
        mapLoaded={mapLoaded}
      />

      {loadingFeatures && (
        <MapBackdrop
          open={loadingFeatures}
          isMobile={isMobile}
          isDesktopViewExpanded={isDesktopViewExpanded}
          mobileDrawerState={mobileDrawerState}
          showSpinner
        />
      )}
    </>
  );
}
